import React from "react";
import { HelpCircle } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  DIAGRAM_KEYS, diagramForDroneType, isAmbiguousCatalogDroneType, CharacteristicDimensionDiagram,
} from "./CharacteristicDimensionDiagrams";

/**
 * SORA 2.5 Table 2 "maximum characteristic dimension" input + contextual help.
 * Construction/type is taken from the linked drone-catalog entry's drone_type when it unambiguously
 * determines a diagram (fixed-wing, helicopter, multirotor — auto-picked); a "vtol" catalog type does
 * NOT auto-pick multirotor, since a VTOL/hybrid airframe may use fixed-wing (wingspan) or multirotor
 * (blade-tip) geometry and the bare catalog value alone can't tell which. In that case, or when no
 * catalog type is known at all, the user gets an explicit manual diagram selector — a UI aid only, never
 * persisted, never used to infer the numeric value itself.
 */
export const CharacteristicDimensionField = ({
  t,
  value,
  onChange,
  required,
  lockedHelper,
  catalogDroneType,
  manualDiagramKey,
  onManualDiagramKeyChange,
  inputId = "ac_max_characteristic_dimension",
}) => {
  const autoDiagramKey = diagramForDroneType(catalogDroneType);
  const activeDiagramKey = autoDiagramKey || manualDiagramKey;
  const isVtolAmbiguous = !autoDiagramKey && isAmbiguousCatalogDroneType(catalogDroneType);
  const diagramLabels = {
    fixed_wing: t.fleet?.diagramFixedWing || "Fixed-wing — wingspan",
    helicopter: t.fleet?.diagramHelicopter || "Helicopter — main rotor diameter",
    multirotor: t.fleet?.diagramMultirotor || "Multirotor — farthest blade-tip distance",
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={inputId}>
          {t.fleet?.maxCharacteristicDimensionM || "Maximum characteristic dimension (m)"}{required ? " *" : ""}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600"
              aria-label={t.fleet?.maxCharacteristicDimensionHelpTitle || "How to measure the characteristic dimension"}
              data-testid="characteristic-dimension-help-trigger"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start" data-testid="characteristic-dimension-help-popover">
            <p className="text-sm font-medium mb-2">
              {t.fleet?.maxCharacteristicDimensionHelpTitle || "How to measure the characteristic dimension"}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {DIAGRAM_KEYS.map((key) => (
                <div key={key} className={`rounded-md border p-1.5 ${activeDiagramKey === key ? "border-blue-400 bg-blue-50 dark:bg-blue-950/30" : "border-slate-200 dark:border-slate-700"}`}>
                  <CharacteristicDimensionDiagram diagramKey={key} label={diagramLabels[key]} />
                  <p className="text-[10px] leading-tight text-center mt-1 text-slate-500">{diagramLabels[key]}</p>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Input
        id={inputId}
        type="number"
        // 2026-09-16, user request: "0.01" rejected a real spec value like 0.3801 (DJI Mavic 3 Pro)
        // as a step mismatch — HTML5 constraint validation then blocks the browser's own native
        // validation bubble in the BROWSER's language, not the app's, before any of this file's own
        // localized checks ever run (see Aircraft.js's handleSubmit, now form-level noValidate for
        // exactly this reason). "any" removes the step constraint; min="0.01" (still enforced by the
        // app's own JS below) keeps the "must be positive" rule.
        step="any"
        min="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid="aircraft-max-characteristic-dimension"
      />
      <p className="text-xs text-[var(--text-muted)]">
        {lockedHelper || t.fleet?.maxCharacteristicDimensionHelper || "Wingspan, main rotor diameter, or farthest distance between rotor blade tips — an input for the more detailed SORA 2.5 risk calculation."}
      </p>

      {autoDiagramKey ? (
        <p className="text-xs text-slate-500">
          {(t.fleet?.detectedFromCatalog || "Detected from the drone catalog: {type}")
            .replace("{type}", t.fleet?.droneTypes?.[autoDiagramKey] || catalogDroneType)}
        </p>
      ) : (
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500">
            {isVtolAmbiguous
              ? (t.fleet?.vtolAmbiguousHelper || "This VTOL aircraft could be built around either fixed-wing or multirotor geometry — pick the one that matches its actual construction:")
              : (t.fleet?.selectAircraftType || "Select the aircraft's construction to see the matching diagram:")}
          </p>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t.fleet?.selectAircraftType}>
            {DIAGRAM_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={manualDiagramKey === key}
                onClick={() => onManualDiagramKeyChange(manualDiagramKey === key ? null : key)}
                className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                  manualDiagramKey === key
                    ? "border-blue-400 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
                    : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                }`}
                data-testid={`characteristic-dimension-select-${key}`}
              >
                {t.fleet?.droneTypes?.[key] || key}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeDiagramKey && (
        <div className="w-24 mx-0">
          <CharacteristicDimensionDiagram diagramKey={activeDiagramKey} label={diagramLabels[activeDiagramKey]} />
        </div>
      )}
    </div>
  );
};
